import { gqlClient } from '@/api/graphql-client';
import { graphql } from '@/gql';
import {
  CustomerCustomFieldAuditLogsQuery,
  PaginationInput,
} from '@/gql/graphql';
import { CustomFieldDefinition } from '@payaca/custom-fields';
import { useQuery } from '@tanstack/react-query';
import customerKeys from './keyFactory';

const GetCustomerCustomFieldAuditLogs = graphql(`
  query CustomerCustomFieldAuditLogs(
    $customerId: ID!
    $identifier: String
    $pagination: PaginationInput
  ) {
    customer(id: $customerId) {
      customField(identifier: $identifier) {
        field {
          schema
        }
        auditLogs(pagination: $pagination) {
          limit
          offset
          totalCount
          items {
            id
            prevValue
            value
            timestamp
            metadata {
              source {
                type
              }
            }
            field {
              schema
            }
            actingUser {
              __typename
              ... on MissingUser {
                message
              }
              ... on User {
                id
                fullName
                avatarUrl
                colour
                firstName
                lastName
                email
              }
            }
          }
        }
      }
    }
  }
`);

export type CustomerCustomFieldAuditLogsPage = NonNullable<
  NonNullable<
    CustomerCustomFieldAuditLogsQuery['customer']['customField']
  >['auditLogs']
>;

type ProjectCustomFieldAuditLog = NonNullable<
  CustomerCustomFieldAuditLogsPage['items'][number]
>;

type ActingUser =
  CustomerCustomFieldAuditLogsPage['items'][number]['actingUser'];

const useGetCustomerCustomFieldAuditLogs = (
  customerId: string,
  fieldIdentifier: string,
  pagination?: PaginationInput
) => {
  const { data, ...rest } = useQuery({
    enabled: !!customerId && !!fieldIdentifier,
    queryKey: customerKeys.customFieldAuditLogs(
      customerId,
      fieldIdentifier,
      pagination
    ),
    queryFn: () => {
      return gqlClient.request(GetCustomerCustomFieldAuditLogs, {
        customerId: customerId,
        identifier: fieldIdentifier,
        pagination,
      });
    },
  });

  const schema = data?.customer?.customField?.field.schema;

  return {
    auditLogs: data?.customer?.customField?.auditLogs,
    field: schema ? CustomFieldDefinition.fromSchema(schema) : null,
    data,
    ...rest,
  };
};

export default useGetCustomerCustomFieldAuditLogs;
